// Small, restrained 3D effects for the board (docs/04 §10, docs/06 §5): construction dust, a few
// gold motes, an expanding floor ring. Sprites and planes that tween and clean themselves up; no
// bloom, no glare — the art direction keeps glow for rare moments.
import * as THREE from 'three';
import { gsap } from 'gsap';

const textures = new Map<string, THREE.Texture>();

/** A soft round puff (dust, smoke): a radial falloff with a slightly lumpy edge. */
function puffTexture(): THREE.Texture {
  let t = textures.get('puff');
  if (t) return t;
  const S = 128;
  const c = document.createElement('canvas');
  c.width = c.height = S;
  const g = c.getContext('2d')!;
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2;
    const r = S * (0.16 + (i % 3) * 0.03);
    const x = S / 2 + Math.cos(a) * S * 0.1;
    const y = S / 2 + Math.sin(a) * S * 0.1;
    const grad = g.createRadialGradient(x, y, 0, x, y, r * 1.6);
    grad.addColorStop(0, 'rgba(255,255,255,0.55)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, S, S);
  }
  t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  textures.set('puff', t);
  return t;
}

/** A four-pointed glint with a soft core (gold motes, the coin's flash). */
function glintTexture(): THREE.Texture {
  let t = textures.get('glint');
  if (t) return t;
  const S = 64;
  const c = document.createElement('canvas');
  c.width = c.height = S;
  const g = c.getContext('2d')!;
  const core = g.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
  core.addColorStop(0, 'rgba(255,255,255,1)');
  core.addColorStop(0.18, 'rgba(255,255,255,0.7)');
  core.addColorStop(0.45, 'rgba(255,255,255,0.1)');
  core.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = core;
  g.fillRect(0, 0, S, S);
  g.fillStyle = 'rgba(255,255,255,0.8)';
  for (const [w, h] of [
    [2.4, S * 0.46],
    [S * 0.46, 2.4],
  ] as const) {
    g.beginPath();
    g.ellipse(S / 2, S / 2, w, h, 0, 0, Math.PI * 2);
    g.fill();
  }
  t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  textures.set('glint', t);
  return t;
}

function sprite(map: THREE.Texture, color: string, opacity: number, additive: boolean): THREE.Sprite {
  const mat = new THREE.SpriteMaterial({ map, color, transparent: true, opacity, depthWrite: false, blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending, toneMapped: false });
  const s = new THREE.Sprite(mat);
  s.renderOrder = 5;
  return s;
}

function done(parent: THREE.Object3D, o: THREE.Sprite | THREE.Mesh): void {
  parent.remove(o);
  (o.material as THREE.Material).dispose();
  if ((o as THREE.Mesh).isMesh) (o as THREE.Mesh).geometry.dispose();
}

/** A deterministic-enough jitter (effects only; never game state). */
const jitter = (k = 1) => (Math.random() - 0.5) * 2 * k;

/**
 * A puff of construction dust at a point: a handful of soft sprites that billow out low, rise a
 * little and fade. `size` scales the whole puff.
 */
export function dust(parent: THREE.Object3D, at: THREE.Vector3, opts: { count?: number; size?: number; color?: string; speed?: number; reduced?: boolean } = {}): void {
  const n = opts.reduced ? 2 : (opts.count ?? 6);
  const size = opts.size ?? 1;
  const speed = opts.speed ?? 1;
  for (let i = 0; i < n; i++) {
    const s = sprite(puffTexture(), opts.color ?? '#cdb896', 0.62, false);
    s.position.copy(at).add(new THREE.Vector3(jitter(0.25 * size), 0.15 * size, jitter(0.25 * size)));
    const s0 = (0.5 + Math.random() * 0.4) * size;
    s.scale.setScalar(s0);
    parent.add(s);
    const dir = new THREE.Vector3(jitter(), 0, jitter()).normalize().multiplyScalar((0.7 + Math.random() * 0.8) * size);
    const dur = (0.8 + Math.random() * 0.5) / speed;
    gsap.to(s.position, { x: s.position.x + dir.x, z: s.position.z + dir.z, y: s.position.y + (0.35 + Math.random() * 0.4) * size, duration: dur, ease: 'power2.out' });
    gsap.to(s.scale, { x: s0 * 2.4, y: s0 * 2.4, duration: dur, ease: 'power1.out' });
    gsap.to(s.material, { opacity: 0, duration: dur, ease: 'power1.in', onComplete: () => done(parent, s) });
  }
}

/**
 * A few gold motes rising from an area — the "it's done" signal. Kept small and brief: they read
 * as a sparkle of dust in the light, not as a firework.
 */
export function motes(parent: THREE.Object3D, at: THREE.Vector3, opts: { count?: number; spread?: number; color?: string; rise?: number; speed?: number; reduced?: boolean } = {}): void {
  const n = opts.reduced ? 3 : (opts.count ?? 12);
  const spread = opts.spread ?? 1.5;
  const speed = opts.speed ?? 1;
  for (let i = 0; i < n; i++) {
    const s = sprite(glintTexture(), opts.color ?? '#ffe3a0', 0, true);
    s.position.copy(at).add(new THREE.Vector3(jitter(spread), Math.random() * 0.4, jitter(spread)));
    const k = 0.22 + Math.random() * 0.22;
    s.scale.setScalar(k);
    parent.add(s);
    const delay = (Math.random() * 0.35) / speed;
    const dur = (0.9 + Math.random() * 0.7) / speed;
    gsap.to(s.position, { y: s.position.y + (opts.rise ?? 2.2) * (0.6 + Math.random() * 0.6), x: s.position.x + jitter(0.3), delay, duration: dur, ease: 'power1.out' });
    gsap
      .timeline({ delay, onComplete: () => done(parent, s) })
      .to(s.material, { opacity: 0.85, duration: dur * 0.25, ease: 'power2.out' })
      .to(s.material, { opacity: 0, duration: dur * 0.75, ease: 'power1.in' });
  }
}

/** One bright glint that pops and fades (a coin landing heads up, a sign lighting). */
export function glint(parent: THREE.Object3D, at: THREE.Vector3, opts: { size?: number; color?: string; speed?: number } = {}): void {
  const s = sprite(glintTexture(), opts.color ?? '#fff1c4', 0.95, true);
  s.position.copy(at);
  const k = opts.size ?? 2.4;
  s.scale.setScalar(k * 0.2);
  parent.add(s);
  const dur = 0.55 / (opts.speed ?? 1);
  gsap.to(s.scale, { x: k, y: k, duration: dur * 0.4, ease: 'expo.out' });
  gsap.to(s.material, { opacity: 0, duration: dur, ease: 'power2.in', onComplete: () => done(parent, s) });
  gsap.to(s.material, { rotation: 0.6, duration: dur });
}

/** An expanding ring on a floor (a room lighting up, a coin's flash). */
export function floorRing(parent: THREE.Object3D, at: THREE.Vector3, opts: { color?: string; radius?: number; speed?: number; opacity?: number } = {}): Promise<void> {
  const mat = new THREE.MeshBasicMaterial({ color: opts.color ?? '#ffd98a', transparent: true, opacity: opts.opacity ?? 0.75, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, toneMapped: false });
  const ring = new THREE.Mesh(new THREE.RingGeometry(0.82, 1, 64), mat);
  ring.rotation.x = -Math.PI / 2;
  ring.position.copy(at);
  ring.renderOrder = 4;
  parent.add(ring);
  const r = opts.radius ?? 4;
  const dur = 0.8 / (opts.speed ?? 1);
  return new Promise((resolve) => {
    gsap.fromTo(ring.scale, { x: 0.3, y: 0.3, z: 0.3 }, { x: r, y: r, z: r, duration: dur, ease: 'expo.out' });
    gsap.to(mat, { opacity: 0, duration: dur, ease: 'power2.in', onComplete: () => (done(parent, ring), resolve()) });
  });
}

export function disposeFx3d(): void {
  for (const t of textures.values()) t.dispose();
  textures.clear();
}
